import React, { useCallback } from 'react';

import { ClassName, GADataType, I18nText } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import { TeamPath } from '../../../../teams/TeamPath';

import {
  LifestyleID,
  LifestyleImageFile,
  LifestyleImageID,
  LifestyleImageUUID
} from '../../../../lifestyles/lifestylesTypes';
import { LifestyleDetectionParams } from '../../../../lifestyleDetections/lifestyleDetectionsTypes';

import { useRouter } from 'next/router';
import { useReactQueryState } from '../../../../common/reactQuery/hooks/useReactQueryState';

import { PureTooltipIconButtonHelper } from '../../../../../helpers/buttons/PureTooltipIconButtonHelper';

import { LifestyleDetectionCache } from '../../../../lifestyleDetections/LifestyleDetectionCache';
import { StockPath } from '../../../../stock/StockPath';

import { aiSearchKeys } from '../../../../../locales/keys';

interface LifestyleSearchProductsButtonLifestyle {
  id: LifestyleID;
  image: {
    id: LifestyleImageID;
    uuid: LifestyleImageUUID;
    file: LifestyleImageFile;
  };
}

interface LifestyleSearchProductsButtonProps {
  lifestyle: LifestyleSearchProductsButtonLifestyle;
  className?: ClassName;
  i18nText?: I18nText;
  i18nTextClassName?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  companyNanoId?: TeamNanoID;
  isMyLibrary?: boolean;
}

function LifestyleSearchProductsButton({
  dataGa,
  lifestyle,
  className,
  i18nText,
  i18nTextClassName,
  icon = IconsEnum.PHOTO_SOLID,
  iconClassName = 'h-5 w-5',
  companyNanoId,
  isMyLibrary
}: LifestyleSearchProductsButtonProps & GADataType) {
  const { push } = useRouter();

  const { setValue: setSearchImageUrl } =
    useReactQueryState<LifestyleDetectionParams>(
      LifestyleDetectionCache.searchImageUrlCacheKey()
    );

  const handleLifestyleSearchProducts = useCallback<() => void>(() => {
    if (lifestyle.image?.file) {
      setSearchImageUrl({
        imageUrl: lifestyle.image.file,
        imageId: lifestyle.image.id,
        lifestyleId: lifestyle.id
      });

      const searchPath = isMyLibrary
        ? TeamPath.currentCompanyNewLibraryProducts()
        : companyNanoId
        ? TeamPath.companyNewLibraryProductsPure(companyNanoId)
        : StockPath.newProducts();

      push(searchPath);
    }
  }, [lifestyle, push, setSearchImageUrl, companyNanoId, isMyLibrary]);

  return (
    <PureTooltipIconButtonHelper
      className={
        className ||
        'bg-white dark:bg-gray-900 dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0 font-medium hover:bg-gray-200 inline-flex items-center p-1 rounded-md text-gray-700 text-sm pointer-events-auto shadow dark:glow'
      }
      dataGa={dataGa}
      icon={icon}
      iconClassName={iconClassName}
      i18nText={i18nText}
      i18nTextClassName={i18nTextClassName}
      onClick={handleLifestyleSearchProducts}
      tooltipI18nText={i18nText ? null : aiSearchKeys.findSimilar}
      tooltipSingleton
    />
  );
}

export default LifestyleSearchProductsButton;
